/**
 * ModulesNudge — the one-time legacy nudge (mod-4 · docs/MODULES.md Sec. 5).
 *
 * A single dismissible dashboard card, shown only to a LEGACY org (never picked
 * a module set) whose admin can `manage_org`. "Choose modules" opens the gallery;
 * "Keep everything" records the interaction (`modules_seeded_at`) without writing
 * a list — the org stays legacy-all-on and the nudge (and the Get-Started step)
 * go quiet. Renders nothing once seeded, so legacy orgs that never interact see
 * zero change.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Boxes, X, ArrowRight } from 'lucide-react';
import { useModules } from '../hooks/useModules';
import { useAuth } from '../hooks/useAuth';
import { roleHasCapability } from '@/shared/lib/capabilities';

export function ModulesNudge() {
    const navigate = useNavigate();
    const { ready, isLegacy, seededAt, markSeeded } = useModules();
    const currentOrg = useAuth((s) => s.currentOrg);
    const canManage = roleHasCapability((currentOrg as { role?: string } | null)?.role ?? '', 'manage_org');
    const [busy, setBusy] = useState(false);

    if (!ready || !isLegacy || seededAt || !canManage) return null;

    const keepAll = async () => {
        setBusy(true);
        await markSeeded();
        setBusy(false);
    };

    return (
        <div className="relative flex items-start gap-3 rounded-lg border border-teal-200 dark:border-teal-900/50 bg-teal-50/80 dark:bg-teal-950/20 px-4 py-3.5 mb-5">
            <div className="w-9 h-9 flex items-center justify-center shrink-0">
                <Boxes size={18} className="text-teal-600 dark:text-teal-400" />
            </div>
            <div className="flex-1 min-w-0 pr-6">
                <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">Tune Karr to your business</p>
                <p className="text-[13px] text-stone-500 dark:text-stone-400 mt-0.5 leading-relaxed">
                    You're seeing every feature right now. Choose the modules your business uses and hide the rest —
                    nothing is deleted, and you can switch anything back on anytime.
                </p>
                <div className="mt-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate({ to: '/modules' })}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors"
                    >
                        Choose modules <ArrowRight size={13} />
                    </button>
                    <button
                        onClick={keepAll}
                        disabled={busy}
                        className="h-8 px-3 rounded-lg text-xs font-medium text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 disabled:opacity-50 transition-colors"
                    >
                        Keep everything
                    </button>
                </div>
            </div>
            <button
                onClick={keepAll}
                disabled={busy}
                aria-label="Dismiss"
                className="absolute top-3 right-3 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 disabled:opacity-50"
            >
                <X size={16} />
            </button>
        </div>
    );
}
